import { 
  clearFormattingEffect,
  setFormattingModeEffect,
  isFormatActive,
  toggleSubscript,
  toggleSuperscript
} from '../extensions/codemirror/textFormatting.js';

/**
 * Adapter class to provide a unified interface for text formatting
 * across different editor implementations (TipTap and CodeMirror)
 */
export class EditorFormatAdapter {
  constructor(editor, type) {
    this.editor = editor;
    this.type = type; // 'tiptap' or 'codemirror'
  }

  /**
   * Check if the editor can apply a specific format
   */
  canFormat(format) {
    if (!this.editor) return false;
    
    if (this.type === 'tiptap') {
      // TipTap: Check if command is available
      try {
        if (!this.editor.can) return false;
        const commandName = this.getTipTapCommand(format);
        return this.editor.can()[commandName]?.() ?? false;
      } catch (e) {
        return false;
      }
    } else {
      // CodeMirror: Always allow formatting (supports both selection and toggle mode)
      try {
        // Always return true for supported formats
        return ['subscript', 'superscript', 'normal', 'clear'].includes(format.toLowerCase());
      } catch (e) {
        return false;
      }
    }
  }

  /**
   * Check if a format is currently active at the cursor
   */
  isActive(format) {
    if (!this.editor) return false;
    
    if (this.type === 'tiptap') {
      // TipTap: Use built-in isActive
      try {
        if (!this.editor.isActive) return false;
        return this.editor.isActive(format.toLowerCase());
      } catch (e) {
        return false;
      }
    } else {
      // CodeMirror: Check decorations at cursor
      try {
        return isFormatActive(this.editor, format.toLowerCase());
      } catch (e) {
        // If the editor doesn't have the formatting field, it's not active
        return false;
      }
    }
  }

  /**
   * Toggle a formatting option
   */
  toggleFormat(format) {
    if (!this.editor) return;
    
    if (this.type === 'tiptap') {
      // TipTap: Use chain API
      const commandName = this.getTipTapCommand(format);
      this.editor.chain().focus()[commandName]().run();
    } else {
      // CodeMirror: Handle both selection and toggle mode
      const formatLower = format.toLowerCase();
      
      // Use the imported toggle functions
      if (formatLower === 'subscript') {
        toggleSubscript(this.editor);
      } else if (formatLower === 'superscript') {
        toggleSuperscript(this.editor);
      } else if (formatLower === 'normal' || formatLower === 'clear') {
        const selection = this.editor.state.selection.main;
        if (!selection.empty) {
          this.editor.dispatch({
            effects: clearFormattingEffect.of({
              from: selection.from,
              to: selection.to
            })
          });
        }
        // Also clear any active formatting mode
        this.editor.dispatch({
          effects: setFormattingModeEffect.of(null)
        });
      }
      
      this.editor.focus();
    }
  }

  /**
   * Remove all formatting (subscript/superscript)
   */
  clearFormatting() {
    if (!this.editor) return;
    
    if (this.type === 'tiptap') {
      // TipTap: Unset both marks
      this.editor.chain()
        .focus()
        .unsetMark('subscript')
        .unsetMark('superscript')
        .run();
    } else {
      // CodeMirror: Clear formatting and any active mode
      const selection = this.editor.state.selection.main;
      if (!selection.empty) {
        this.editor.dispatch({
          effects: clearFormattingEffect.of({
            from: selection.from,
            to: selection.to
          })
        });
      }
      // Clear any active formatting mode
      this.editor.dispatch({
        effects: setFormattingModeEffect.of(null)
      });
      this.editor.focus();
    }
  }

  /**
   * Get the available formatting options for this editor
   */
  getAvailableFormats() {
    if (this.type === 'tiptap') {
      return ['italic', 'underline', 'subscript', 'superscript', 'normal'];
    } else {
      return ['subscript', 'superscript', 'normal'];
    }
  }

  /**
   * Check if this editor supports a specific format
   */
  supportsFormat(format) {
    const available = this.getAvailableFormats();
    return available.includes(format.toLowerCase());
  }

  /**
   * Focus the editor
   */
  focus() {
    if (!this.editor) return;
    
    if (this.type === 'tiptap') {
      this.editor.chain().focus().run();
    } else {
      this.editor.focus();
    }
  }

  /**
   * Helper to get TipTap command name
   */
  getTipTapCommand(format) {
    const formatLower = format.toLowerCase();
    if (formatLower === 'normal') {
      return 'unsetAllMarks';
    }
    return `toggle${format.charAt(0).toUpperCase() + format.slice(1).toLowerCase()}`;
  }
}

/**
 * Factory function to create an adapter based on editor type
 */
export function createEditorAdapter(editor, type) {
  if (!editor || !type) return null;
  return new EditorFormatAdapter(editor, type);
}